import { SystemFormData } from "./types";

export const SYSTEM_TYPES = [
  "Intake System",
  "Raw Water Transmission System",
  "Pre-treatment System",
  "Filtration System",
  "Disinfection System",
  "Post-treatment & Storage System",
  "Pumping System (Plant to Distribution)",
  "Water Distribution System",
  "SCADA & Instrumentation System",
  "Sludge Management System",
  "Other",
];

export const generateSystemId = (systemType: string): any => {
  const typeMap: Record<string, string> = {
    "Intake System": "INT",
    "Raw Water Transmission System": "RWT",
    "Pre-treatment System": "PRT",
    "Filtration System": "FLT",
    "Disinfection System": "DIS",
    "Post-treatment & Storage System": "PTS",
    "Pumping System (Plant to Distribution)": "PMP",
    "Water Distribution System": "WDS",
    "SCADA & Instrumentation System": "SCA",
    "Sludge Management System": "SLG",
    Other: "OTH",
  };
};

export const CAPACITY_UNITS = [
  { value: 'MGD', label: 'MGD (Million Gallons per Day)' },
  { value: 'm³/d', label: 'm³/d (Cubic Meters per Day)' },
  { value: 'L/s', label: 'L/s (Liters per Second)' }
];

export const DEFAULT_PARAMETERS = {
  'Intake System': [
    { name: 'Raw Water Turbidity', unit: 'NTU', value: '' },
    { name: 'Raw Water pH', unit: '', value: '' },
    { name: 'Raw Water Temperature', unit: '°C/°F', value: '' },
    { name: 'Raw Water Dissolved Oxygen', unit: 'mg/L', value: '' },
    { name: 'Raw Water Conductivity', unit: 'µS/cm', value: '' },
    { name: 'Raw Water Flow Rate', unit: 'MGD/LPS', value: '' },
    { name: 'Algae Count', unit: 'cells/mL', value: '' },
    { name: 'Specific Contaminant Concentrations', unit: 'mg/L', value: '' }
  ],
  'Raw Water Transmission System': [
    { name: 'Flow Rate', unit: 'MGD/LPS', value: '' },
    { name: 'Pressure', unit: 'psi/kPa', value: '' },
    { name: 'Water Level', unit: 'ft/m', value: '' }
  ],
  'Pre-treatment System': [
    { name: 'Influent Turbidity', unit: 'NTU', value: '' },
    { name: 'Effluent Turbidity', unit: 'NTU', value: '' },
    { name: 'Coagulant Dose', unit: 'mg/L', value: '' },
    { name: 'pH (at various stages)', unit: '', value: '' },
    { name: 'Flocculation Basin Mixer Speed', unit: 'RPM', value: '' },
    { name: 'Sludge Blanket Level', unit: 'ft/m', value: '' },
    { name: 'Flow Rate', unit: 'MGD/LPS', value: '' }
  ],
  'Filtration System': [
    { name: 'Influent Turbidity', unit: 'NTU', value: '' },
    { name: 'Effluent Turbidity', unit: 'NTU', value: '' },
    { name: 'Head Loss', unit: 'ft/m', value: '' },
    { name: 'Flow Rate', unit: 'MGD/LPS', value: '' },
    { name: 'Particle Count', unit: 'particles/mL', value: '' },
    { name: 'Backwash Flow Rate', unit: 'MGD/LPS', value: '' },
    { name: 'Transmembrane Pressure (TMP)', unit: 'psi/kPa', value: '' }
  ],
  'Disinfection System': [
    { name: 'Disinfectant Dose', unit: 'mg/L', value: '' },
    { name: 'Disinfectant Residual', unit: 'mg/L', value: '' },
    { name: 'pH', unit: '', value: '' },
    { name: 'Turbidity (pre-disinfection)', unit: 'NTU', value: '' },
    { name: 'UV Transmittance', unit: '%', value: '' },
    { name: 'Ozone Concentration', unit: 'mg/L', value: '' },
    { name: 'DBP Concentrations (THMs, HAAs)', unit: 'µg/L', value: '' }
  ],
  'Post-treatment & Storage System': [
    { name: 'Disinfectant Residual', unit: 'mg/L', value: '' },
    { name: 'pH', unit: '', value: '' },
    { name: 'Temperature', unit: '°C/°F', value: '' },
    { name: 'Water Level', unit: 'ft/m', value: '' },
    { name: 'Fluoride Concentration', unit: 'mg/L', value: '' },
    { name: 'Total Organic Carbon (TOC)', unit: 'mg/L', value: '' }
  ],
  'Pumping System (Plant to Distribution)': [
    { name: 'Flow Rate', unit: 'MGD/LPS', value: '' },
    { name: 'Suction Pressure', unit: 'psi/kPa', value: '' },
    { name: 'Discharge Pressure', unit: 'psi/kPa', value: '' },
    { name: 'Motor Amperage', unit: 'Amps', value: '' },
    { name: 'Power Consumption', unit: 'kWh', value: '' },
    { name: 'Vibration', unit: 'mm/s', value: '' },
    { name: 'Temperature', unit: '°C/°F', value: '' }
  ],
  'Water Distribution System': [
    { name: 'Pressure', unit: 'psi/kPa', value: '' },
    { name: 'Disinfectant Residual', unit: 'mg/L', value: '' },
    { name: 'pH', unit: '', value: '' },
    { name: 'Temperature', unit: '°C/°F', value: '' },
    { name: 'Turbidity', unit: 'NTU', value: '' },
    { name: 'Bacteriological Sample Results', unit: 'Presence/Absence', value: '' },
    { name: 'Flow Rate', unit: 'MGD/LPS', value: '' },
    { name: 'Conductivity', unit: 'µS/cm', value: '' }
  ],
  'SCADA & Instrumentation System': [
    { name: 'Data Latency', unit: 'ms', value: '' },
    { name: 'Communication Link Status', unit: 'Up/Down', value: '' },
    { name: 'Sensor Readout Accuracy', unit: '% deviation', value: '' },
    { name: 'Alarm Count', unit: 'per period', value: '' },
    { name: 'System Uptime', unit: '%', value: '' }
  ],
  'Sludge Management System': [
    { name: 'Sludge Volume', unit: 'gallons/liters', value: '' },
    { name: 'Solids Content', unit: '%', value: '' },
    { name: 'Polymer Dose', unit: 'mg/L', value: '' },
    { name: 'pH', unit: '', value: '' },
    { name: 'Temperature', unit: '°C/°F', value: '' },
    { name: 'Chemical Oxygen Demand (COD)', unit: 'mg/L', value: '' },
    { name: 'Biochemical Oxygen Demand (BOD)', unit: 'mg/L', value: '' },
    { name: 'Total Suspended Solids (TSS)', unit: 'mg/L', value: '' }
  ],
  'Other': []
};

export const CRITICALITY_MATRIX = {
  'Intake System': { impactOnSupply: 5, healthRisk: 5, redundancy: 1, dependency: 4, customerImpact: 5 },
  'Raw Water Transmission System': { impactOnSupply: 4, healthRisk: 4, redundancy: 2, dependency: 4, customerImpact: 4 },
  'Pre-treatment System': { impactOnSupply: 4, healthRisk: 3, redundancy: 2, dependency: 3, customerImpact: 3 },
  'Filtration System': { impactOnSupply: 5, healthRisk: 5, redundancy: 2, dependency: 4, customerImpact: 4 },
  'Disinfection System': { impactOnSupply: 5, healthRisk: 5, redundancy: 1, dependency: 5, customerImpact: 5 },
  'Post-treatment & Storage System': { impactOnSupply: 4, healthRisk: 3, redundancy: 3, dependency: 3, customerImpact: 3 },
  'Pumping System (Plant to Distribution)': { impactOnSupply: 4, healthRisk: 4, redundancy: 2, dependency: 4, customerImpact: 4 },
  'Water Distribution System': { impactOnSupply: 5, healthRisk: 3, redundancy: 2, dependency: 4, customerImpact: 5 },
  'SCADA & Instrumentation System': { impactOnSupply: 3, healthRisk: 3, redundancy: 3, dependency: 2, customerImpact: 3 },
  'Sludge Management System': { impactOnSupply: 2, healthRisk: 2, redundancy: 4, dependency: 1, customerImpact: 2 },
  'Other': { impactOnSupply: 3, healthRisk: 3, redundancy: 3, dependency: 3, customerImpact: 3 }
};

export const calculateCriticalityScore = (scores: SystemFormData['criticalityScores']) => {
  const weights = {
    impactOnSupply: 0.30,
    healthRisk: 0.25,
    redundancy: 0.15,
    dependency: 0.15,
    customerImpact: 0.15
  };
  
  const totalScore = 
    scores.impactOnSupply * weights.impactOnSupply +
    scores.healthRisk * weights.healthRisk +
    scores.redundancy * weights.redundancy +
    scores.dependency * weights.dependency +
    scores.customerImpact * weights.customerImpact;
    
  return Math.round(totalScore * 100) / 100;
};

export const getCriticalityLevel = (score: number): string => {
  if (score >= 4) return 'High';
  if (score >= 3) return 'Medium';
  if (score >= 2) return 'Medium';
  if (score >= 1) return 'Low';
  return 'Low';
};
